import {
  CONTENT_REGIONS,
  formatRoute,
  INVIDIOUS_DASH_HEIGHTS,
  NAV_DESTINATIONS,
  normalizeInstanceUrl,
  normalizeLanding,
  normalizeUiLocale,
  PLAYBACK_RATES,
  normalizeSponsorColor,
  probeBlocksUse,
  SETTINGS_TABS,
  SPONSOR_CATEGORIES,
  type CapabilityState,
  type InstanceProbeReport,
  type InstanceSource,
  type InstanceUrlProblem,
  type DeArrowSettings,
  type LandingId,
  type ProbeFinding,
  type ProbeStepId,
  type ResolvedLocale,
  type ReturnYoutubeDislikeSettings,
  type PeerTubeSettings,
  type SettingsSyncState,
  type SettingsTab,
  type SponsorBlockSettings,
  type SponsorCategory,
  type SponsorCategorySettings,
  type SponsorSkipBehavior,
  type ThemeChoice,
  type VisiousSettings,
  UI_LOCALES,
} from '@visious/core';
import type { ProbeStepResult } from '@visious/core';
import type { PreferencesProblem } from '../../api/preferences';
import { describeAuthProblem, type ProblemCopy } from '../../api/problem';
import type { MenuModel } from '../../ui/menuModel';
import { navLabels } from '../../shell/model';
import { text } from '../../strings';

/**
 * §8's Settings page, as reasoning — R13: no DOM, no React, no `.tsx` import.
 *
 * It is the last unbuilt page and the only one whose subject is *the app itself* rather than a payload,
 * which changes what "logic" means here: there is no parse, no paging and no hydration, and instead
 * three things that have to be decided rather than rendered.
 *
 * 1. **Every string.** R7 keeps display strings out of core, so core's probe answers with finding ids
 *    and its capability record with `serves | disabled | unknown` — and the sentences for all of them
 *    live in this file. That is what makes them reviewable as copy rather than scattered through JSX.
 * 2. **What is safe to offer.** The region list is core's 107-code allowlist rather than ISO-3166,
 *    because the other 142 codes answer `500`; the quality list is Invidious's own `quality_dash`
 *    options, so a pinned height is one both clients can show; the page size is bounded by the
 *    instance's own clamp.
 * 3. **What a control must say about itself.** Four settings do not follow the reader to another device
 *    and two of those *cannot* — `SYNC_NOTE` is where each one says so, next to the control, instead of
 *    in a footnote nobody reads.
 */

// Re-exported for the view, which must not import core itself (R13, `tsx-no-core-import`).
export type { LandingId, SettingsTab, SponsorCategory };

// ---------------------------------------------------------------------------
// The tabs
// ---------------------------------------------------------------------------

export interface SettingsTabLink {
  readonly tab: SettingsTab;
  readonly label: string;
  readonly href: string;
  readonly current: boolean;
}

/** The strip, the channel page's shape: a tab is a `<Link>`, and Back means "the tab I was on". */
export function settingsTabLinks(current: SettingsTab): readonly SettingsTabLink[] {
  const labels = text().settings.tab;
  return SETTINGS_TABS.map((tab) => ({
    tab,
    label: labels[tab],
    href: formatRoute({ kind: 'settings', tab }),
    current: tab === current,
  }));
}

// ---------------------------------------------------------------------------
// The instance section
// ---------------------------------------------------------------------------

/**
 * §3's four resolution steps, in the words a reader can act on.
 *
 * Functions rather than constants throughout this file, which is the shape 4e's catalogue forced: a
 * module-level `Record` freezes whichever language happened to be active when the module first loaded.
 */
export function instanceSourceCopy(): Readonly<Record<InstanceSource, string>> {
  return text().settings.instanceSource;
}

export function instanceUrlProblemCopy(): Readonly<Record<InstanceUrlProblem, string>> {
  const words = text().settings.instanceUrlProblem;
  return {
    empty: words.empty,
    'not-absolute': words.notAbsolute,
    'unsupported-scheme': words.unsupportedScheme,
    'has-query': words.hasQuery,
  };
}

export interface InstanceFieldState {
  readonly value: string;
  /** `null` while the text is not yet a usable instance URL. */
  readonly normalized: string | null;
  readonly problem: string | null;
  /** The typed address is the one already in use, so there is nothing to switch to. */
  readonly unchanged: boolean;
}

export function instanceFieldState(input: string, current: string): InstanceFieldState {
  const trimmed = input.trim();
  if (trimmed === '') {
    return { value: input, normalized: null, problem: null, unchanged: false };
  }

  const result = normalizeInstanceUrl(trimmed);
  if (!result.ok) {
    return {
      value: input,
      normalized: null,
      problem: instanceUrlProblemCopy()[result.error],
      unchanged: false,
    };
  }
  return {
    value: input,
    normalized: result.value,
    problem: null,
    unchanged: result.value === current,
  };
}

// ---------------------------------------------------------------------------
// The probe, in words
// ---------------------------------------------------------------------------

export function probeStepLabel(): Readonly<Record<ProbeStepId, string>> {
  return text().settings.probeStep;
}

/**
 * One sentence per finding id — every one a measured state rather than a category.
 *
 * **Partial on purpose.** A finding core reports is a fact worth recording; it is not automatically
 * worth a line on screen. `statistics-disabled` and `captions-companion-path` are the two states
 * that need no sentence — the first changes nothing the reader can act on, the second is the
 * ordinary case — so they are absent here and `probeStepRows` drops them from the notes instead of
 * printing an empty bullet. Core keeps emitting both.
 */
export function probeFindingCopy(): Readonly<Partial<Record<ProbeFinding, string>>> {
  const words = text().settings.probeFinding;
  return {
    unreachable: words.unreachable,
    'cors-blocked': words.corsBlocked,
    aborted: words.aborted,
    'http-error': words.httpError,
    'empty-body': words.emptyBody,
    'not-json': words.notJson,
    'not-invidious': words.notInvidious,
    'no-adaptive-formats': words.noAdaptiveFormats,
    'no-dash-manifest': words.noDashManifest,
    'manifest-unreadable': words.manifestUnreadable,
    'no-captions-listed': words.noCaptionsListed,
    'popular-disabled': words.popularDisabled,
    'auth-absent': words.authAbsent,
    'captions-documented-path': words.captionsDocumentedPath,
    'captions-unavailable': words.captionsUnavailable,
  };
}

export function capabilityCopy(): Readonly<Record<CapabilityState, string>> {
  const words = text().settings.capability;
  return { serves: words.serves, disabled: words.disabled, unknown: words.unknown };
}

export interface CapabilityRow {
  readonly id: string;
  readonly label: string;
  readonly state: CapabilityState;
  readonly value: string;
  readonly detail: string | null;
}

export function capabilityRows(report: InstanceProbeReport): readonly CapabilityRow[] {
  const capabilities = report.capabilities;
  const words = text().settings.capability;
  const value = capabilityCopy();
  return [
    {
      id: 'popular',
      label: words.popular,
      state: capabilities.popular,
      value: value[capabilities.popular],
      detail: capabilities.popular === 'disabled' ? words.popularDisabled : null,
    },
    {
      id: 'trending',
      label: words.trending,
      state: capabilities.trending,
      value: value[capabilities.trending],
      detail: null,
    },
    {
      id: 'adaptiveFormats',
      label: words.adaptiveFormats,
      state: capabilities.adaptiveFormats,
      value: value[capabilities.adaptiveFormats],
      detail: null,
    },
    {
      id: 'dashManifest',
      label: words.dashManifest,
      state: capabilities.dashManifest,
      value: value[capabilities.dashManifest],
      detail: null,
    },
    {
      id: 'auth',
      label: words.auth,
      state: capabilities.auth,
      value: value[capabilities.auth],
      detail: null,
    },
    /*
     * Yes or No, like every row above it. The path the captions came back on used to be the value
     * here, which made this the one row answering a different question from the rest of the grid: a
     * reader scanning a column of Yes and No hit a URL fragment. Which path served them is still what
     * the probe measured and still what step 5's own finding says, in the notes under that step.
     */
    {
      id: 'captions',
      label: words.captions,
      state: capabilities.captionPathPrefix === null ? 'disabled' : 'serves',
      value: capabilities.captionPathPrefix === null ? value.disabled : value.serves,
      detail: capabilities.captionPathPrefix === null ? words.captionsHidden : null,
    },
  ];
}

export interface ProbeStepRow {
  readonly id: ProbeStepId;
  readonly label: string;
  readonly outcome: ProbeStepResult['outcome'];
  readonly status: number | null;
  readonly notes: readonly string[];
}

export function probeStepRows(report: InstanceProbeReport): readonly ProbeStepRow[] {
  const labels = probeStepLabel();
  const findings = probeFindingCopy();
  return report.steps.map((step) => ({
    id: step.id,
    label: labels[step.id],
    outcome: step.outcome,
    status: step.status,
    notes: step.findings
      .map((finding) => findings[finding])
      .filter((note): note is string => note !== undefined),
  }));
}

// ---------------------------------------------------------------------------
// The controls
// ---------------------------------------------------------------------------

export function themeOptions(): readonly { readonly id: ThemeChoice; readonly label: string }[] {
  const words = text().settings.control.theme;
  return [
    { id: 'system', label: words.system },
    { id: 'dark', label: words.dark },
    { id: 'light', label: words.light },
  ];
}

export function landingOptions(): readonly { readonly id: LandingId; readonly label: string }[] {
  const words = text().settings.control.landing;
  return [
    { id: 'popular', label: words.popular },
    { id: 'subscriptions', label: words.subscriptions },
    { id: 'playlists', label: words.playlists },
    { id: 'history', label: words.history },
  ];
}

/**
 * The Menu section's rows — one show/hide toggle per nav destination, spelled the way the nav
 * spells it. Hiding is display-only and local: the pages stay reachable by URL, and the landing
 * select above keeps offering all four.
 *
 * Popular's row is the one with a second input: while the instance has the endpoint switched off the
 * toggle is disabled-off with the reason beside it, because a menu item whose page always answers
 * "the administrator turned this off" is §8's "shown broken" with extra steps.
 */
export interface MenuRow {
  readonly id: LandingId;
  readonly label: string;
  readonly shown: boolean;
  readonly disabled: boolean;
  readonly detail: string | null;
}

export function menuRows(settings: VisiousSettings, popularDisabled: boolean): readonly MenuRow[] {
  const labels = navLabels();
  const words = text().settings.control.menu;
  return NAV_DESTINATIONS.map((destination) => {
    const disabled = destination.id === 'popular' && popularDisabled;
    return {
      id: destination.id,
      label: labels[destination.id],
      shown: !disabled && !settings.hiddenMenu.includes(destination.id),
      disabled,
      detail: disabled ? words.popularDisabled : null,
    };
  });
}

export function menuItemPatch(
  settings: VisiousSettings,
  id: LandingId,
  shown: boolean,
): Partial<VisiousSettings> {
  const without = settings.hiddenMenu.filter((hidden) => hidden !== id);
  return { hiddenMenu: shown ? without : [...without, id] };
}

/**
 * The Menu row's control — the four show/hide toggles folded into one multi-select menu, so the
 * whole choice fits a settings row. The trigger's value counts for the reader ("All shown", "3 of
 * 5"), because a closed menu that says nothing forces an open to check.
 */
export function menuSectionsMenu(settings: VisiousSettings, popularDisabled: boolean): MenuModel {
  const words = text().settings.control.menu;
  const rows = menuRows(settings, popularDisabled);
  const shown = rows.filter((row) => row.shown).length;
  return {
    label: words.label,
    value: shown === rows.length ? words.allShown : words.shownOf(shown, rows.length),
    valueActive: shown !== rows.length,
    sections: [
      {
        title: null,
        items: rows.map((row) => ({
          id: row.id,
          label: row.label,
          detail: row.detail,
          selected: row.shown,
          ...(row.disabled ? { disabled: true } : {}),
        })),
      },
    ],
    note: null,
  };
}

// ---------------------------------------------------------------------------
// The Integrations tab — SponsorBlock
// ---------------------------------------------------------------------------

function sponsorBehaviorLabels(): Readonly<Record<SponsorSkipBehavior, string>> {
  const words = text().settings.sponsorBlock.behavior;
  return { auto: words.auto, toast: words.toast, off: words.off };
}

/** One row of the category grid: the swatch, and everything else folded into one menu. */
export interface SponsorCategoryRow {
  readonly id: SponsorCategory;
  readonly label: string;
  readonly color: string;
  readonly colorLabel: string;
  /**
   * The category's behaviour and its two marks as one control (2026-08-01): a Skip-behavior
   * section of radios over a Show-on section of checkboxes — the audio menu's grammar, where
   * "one of these" and "any of these" share a popover. The trigger prints the behaviour, the
   * control's most consequential answer; the marks are one click away.
   */
  readonly menu: MenuModel;
}

export function sponsorCategoryRows(settings: VisiousSettings): readonly SponsorCategoryRow[] {
  const names = text().sponsorBlock.category;
  const words = text().settings.sponsorBlock.categories;
  const behaviorLabels = sponsorBehaviorLabels();
  return SPONSOR_CATEGORIES.map((id) => {
    const category = settings.sponsorBlock.categories[id];
    const label = names[id];
    return {
      id,
      label,
      color: category.color,
      colorLabel: words.colorLabel(label),
      menu: {
        label,
        value: behaviorLabels[category.behavior],
        sections: [
          {
            title: words.behaviorTitle,
            items: (['auto', 'toast', 'off'] as const).map((behavior) => ({
              id: behavior,
              label: behaviorLabels[behavior],
              detail: null,
              selected: category.behavior === behavior,
            })),
          },
          {
            title: words.showTitle,
            items: [
              {
                id: 'bar',
                label: words.bar,
                detail: null,
                selected: category.showInBar,
                checkbox: true,
              },
              {
                id: 'chapters',
                label: words.chapters,
                detail: null,
                selected: category.showInChapters,
                checkbox: true,
              },
            ],
          },
        ],
        note: null,
      },
    };
  });
}

/**
 * The service-address field, the instance field's shape: `''` is valid and means the official
 * service, anything else must be a usable base URL. The problem sentences are the instance field's
 * own — the `empty` arm can never fire here, since empty is the default rather than a mistake.
 */
export function sponsorApiFieldState(input: string): {
  readonly value: string;
  readonly normalized: string | null;
  readonly problem: string | null;
} {
  const trimmed = input.trim();
  if (trimmed === '') return { value: input, normalized: null, problem: null };
  const result = normalizeInstanceUrl(trimmed);
  if (!result.ok) {
    // The instance field's sentences, except the one that names an Invidious address as its
    // example — this field's own example is a SponsorBlock host.
    const problem =
      result.error === 'not-absolute'
        ? text().settings.sponsorBlock.address.notAbsolute
        : instanceUrlProblemCopy()[result.error];
    return { value: input, normalized: null, problem };
  }
  return { value: input, normalized: result.value, problem: null };
}

function sponsorBlockPatch(
  settings: VisiousSettings,
  patch: Partial<SponsorBlockSettings>,
): Partial<VisiousSettings> {
  return { sponsorBlock: { ...settings.sponsorBlock, ...patch } };
}

/* ------------------------------------------------------------------------------------------------
 * The Integrations tab — DeArrow (2026-08-02). SponsorBlock's shapes: the same patch
 * grammar, and the address fields reuse `sponsorApiFieldState` — a DeArrow server is addressed
 * exactly the way a SponsorBlock one is.
 * ---------------------------------------------------------------------------------------------- */

function deArrowPatch(
  settings: VisiousSettings,
  patch: Partial<DeArrowSettings>,
): Partial<VisiousSettings> {
  return { deArrow: { ...settings.deArrow, ...patch } };
}

export function deArrowEnabledPatch(settings: VisiousSettings, on: boolean): Partial<VisiousSettings> {
  return deArrowPatch(settings, { enabled: on });
}

export function deArrowReplaceThumbnailsPatch(
  settings: VisiousSettings,
  on: boolean,
): Partial<VisiousSettings> {
  return deArrowPatch(settings, { replaceThumbnails: on });
}

/** `null` is the official service — what the empty field means, for both addresses. */
export function deArrowApiUrlPatch(
  settings: VisiousSettings,
  normalized: string | null,
): Partial<VisiousSettings> {
  return deArrowPatch(settings, { apiUrl: normalized });
}

export function deArrowThumbnailApiUrlPatch(
  settings: VisiousSettings,
  normalized: string | null,
): Partial<VisiousSettings> {
  return deArrowPatch(settings, { thumbnailApiUrl: normalized });
}

/* ------------------------------------------------------------------------------------------------
 * The Integrations tab — Return YouTube Dislike. SponsorBlock's simplest shape (no categories, no
 * second toggle): an enable switch and one address field, reusing `sponsorApiFieldState` the way
 * DeArrow's address fields do — a self-hosted RYD server is addressed exactly the way the others are.
 * ---------------------------------------------------------------------------------------------- */

function returnYoutubeDislikePatch(
  settings: VisiousSettings,
  patch: Partial<ReturnYoutubeDislikeSettings>,
): Partial<VisiousSettings> {
  return { returnYoutubeDislike: { ...settings.returnYoutubeDislike, ...patch } };
}

export function returnYoutubeDislikeEnabledPatch(
  settings: VisiousSettings,
  on: boolean,
): Partial<VisiousSettings> {
  return returnYoutubeDislikePatch(settings, { enabled: on });
}

/** `null` is the official service — what the empty field means. */
export function returnYoutubeDislikeApiUrlPatch(
  settings: VisiousSettings,
  normalized: string | null,
): Partial<VisiousSettings> {
  return returnYoutubeDislikePatch(settings, { apiUrl: normalized });
}

/* ------------------------------------------------------------------------------------------------
 * The Integrations tab — PeerTube. SponsorBlock's address-field grammar, plus a mode select the
 * others don't need: which player a matched video plays in.
 * ---------------------------------------------------------------------------------------------- */

function peerTubePatch(
  settings: VisiousSettings,
  patch: Partial<PeerTubeSettings>,
): Partial<VisiousSettings> {
  return { peerTube: { ...settings.peerTube, ...patch } };
}

export function peerTubeEnabledPatch(settings: VisiousSettings, on: boolean): Partial<VisiousSettings> {
  return peerTubePatch(settings, { enabled: on });
}

/** `null` is the default instance — what the empty field means. */
export function peerTubeApiUrlPatch(
  settings: VisiousSettings,
  normalized: string | null,
): Partial<VisiousSettings> {
  return peerTubePatch(settings, { apiUrl: normalized });
}

export function peerTubeModePatch(settings: VisiousSettings, id: string): Partial<VisiousSettings> {
  return peerTubePatch(settings, { mode: id === 'embedded' ? 'embedded' : 'integrated' });
}

export function peerTubeModeOptions(): readonly { readonly id: string; readonly label: string }[] {
  const words = text().settings.peerTube.mode;
  return [
    { id: 'integrated', label: words.integrated },
    { id: 'embedded', label: words.embedded },
  ];
}

export function peerTubeSearchTargetPatch(
  settings: VisiousSettings,
  id: string,
): Partial<VisiousSettings> {
  return peerTubePatch(settings, { searchTarget: id === 'local' ? 'local' : 'search-index' });
}

export function peerTubeSearchTargetOptions(): readonly { readonly id: string; readonly label: string }[] {
  const words = text().settings.peerTube.searchTarget;
  return [
    { id: 'search-index', label: words.searchIndex },
    { id: 'local', label: words.local },
  ];
}

export function sponsorEnabledPatch(settings: VisiousSettings, on: boolean): Partial<VisiousSettings> {
  return sponsorBlockPatch(settings, { enabled: on });
}

/** `null` is the official service — what the empty field means. */
export function sponsorApiUrlPatch(
  settings: VisiousSettings,
  normalized: string | null,
): Partial<VisiousSettings> {
  return sponsorBlockPatch(settings, { apiUrl: normalized });
}

function sponsorCategoryPatch(
  settings: VisiousSettings,
  id: SponsorCategory,
  patch: Partial<SponsorCategorySettings>,
): Partial<VisiousSettings> {
  const categories = settings.sponsorBlock.categories;
  return sponsorBlockPatch(settings, {
    categories: { ...categories, [id]: { ...categories[id], ...patch } },
  });
}

/**
 * One press in a category's menu. A behaviour id *sets* (the section is radios) while a mark id
 * *toggles* (checkboxes) — the same asymmetry the menu announces via the items' roles.
 */
export function sponsorCategoryItemPatch(
  settings: VisiousSettings,
  id: SponsorCategory,
  itemId: string,
): Partial<VisiousSettings> {
  const category = settings.sponsorBlock.categories[id];
  if (itemId === 'auto' || itemId === 'toast' || itemId === 'off') {
    return sponsorCategoryPatch(settings, id, { behavior: itemId });
  }
  if (itemId === 'bar') return sponsorCategoryPatch(settings, id, { showInBar: !category.showInBar });
  if (itemId === 'chapters') {
    return sponsorCategoryPatch(settings, id, { showInChapters: !category.showInChapters });
  }
  return {};
}

export function sponsorCategoryColorPatch(
  settings: VisiousSettings,
  id: SponsorCategory,
  value: string,
): Partial<VisiousSettings> {
  // Through core's own gate, so junk keeps the current colour rather than storing itself. A native
  // colour input only ever emits `#rrggbb`, but this function cannot know its caller is one.
  return sponsorCategoryPatch(settings, id, {
    color: normalizeSponsorColor(value, settings.sponsorBlock.categories[id].color),
  });
}

export function sponsorCategoryBarPatch(
  settings: VisiousSettings,
  id: SponsorCategory,
  on: boolean,
): Partial<VisiousSettings> {
  return sponsorCategoryPatch(settings, id, { showInBar: on });
}

export function sponsorCategoryChaptersPatch(
  settings: VisiousSettings,
  id: SponsorCategory,
  on: boolean,
): Partial<VisiousSettings> {
  return sponsorCategoryPatch(settings, id, { showInChapters: on });
}

/**
 * **4e's Language row.** `''` is "follow the system", and the rest are the languages this build has a
 * catalogue for — read from core's `UI_LOCALES`, so a locale can never be offered without copy behind
 * it (`strings/index.ts`'s `Record<UiLocaleId, Strings>` is the other half of that guarantee).
 */
export const LANGUAGE_FOLLOW_SYSTEM = '';

export function uiLanguageOptions(): readonly { readonly id: string; readonly label: string }[] {
  const words = text().settings.control.language;
  return [
    { id: LANGUAGE_FOLLOW_SYSTEM, label: words.followSystem },
    ...UI_LOCALES.map((id) => ({ id, label: words[id] })),
  ];
}

/**
 * The Language row's two halves: which language is in use and why (the visible line), and the same
 * plus the formatting locale as the row's hover title, because the line under a title is owed exactly
 * one line. The note about what syncing does to a language the catalogue lacks went with the rest of
 * the where-stored prose: on this page that story is the storage icon's, and nowhere else's.
 */
export function uiLanguageRow(locale: ResolvedLocale): { readonly detail: string; readonly title: string } {
  const words = text().settings.control.language;
  const language = words[locale.ui];
  const lead =
    locale.source === 'setting'
      ? words.hintSetting(language)
      : locale.source === 'system'
        ? words.hintSystem(language)
        : words.hintFallback(language);
  return {
    detail: lead,
    title: `${lead} ${words.formatting(locale.formatting)}`,
  };
}

export function uiLanguagePatch(value: string): Partial<VisiousSettings> {
  return { uiLocale: normalizeUiLocale(value) };
}

/** The id `auto` uses in the quality menu — the same word the player's own menu uses. */
export const QUALITY_AUTO = 'auto';

export function qualityOptions(): readonly { readonly id: string; readonly label: string }[] {
  const words = text().settings.control.quality;
  return [
    { id: QUALITY_AUTO, label: words.auto },
    // Invidious's own `quality_dash` heights, so a pin is a value both clients can display. A height
    // outside this list is not offered *and* is not pushed (`preferencesPatchFromSettings`).
    ...INVIDIOUS_DASH_HEIGHTS.map((height) => ({ id: String(height), label: words.height(height) })),
  ];
}

/**
 * A short list of languages for the two track-preference menus, with their tags.
 *
 * **This is platform copy, not a capability claim.** A track preference is matched against whatever
 * languages a given video turned out to have (`decideAudioTrack`, `decideSubtitle`), so no list can be
 * "the right one" — what a menu can do is save typing for the common cases and leave the field able to
 * express anything. These are the languages YouTube's multi-language audio actually ships, read off the
 * captured multi-dub manifest, plus the ones with the largest caption coverage.
 */
export function languageOptions(): readonly { readonly tag: string; readonly label: string }[] {
  const words = text().settings.language;
  return [
    { tag: 'ar', label: words.ar },
    { tag: 'bn', label: words.bn },
    { tag: 'de', label: words.de },
    { tag: 'en', label: words.en },
    { tag: 'es', label: words.es },
    { tag: 'fr', label: words.fr },
    { tag: 'hi', label: words.hi },
    { tag: 'id', label: words.id },
    { tag: 'it', label: words.it },
    { tag: 'ja', label: words.ja },
    { tag: 'ko', label: words.ko },
    { tag: 'nl', label: words.nl },
    { tag: 'pl', label: words.pl },
    { tag: 'pt', label: words.pt },
    { tag: 'ru', label: words.ru },
    { tag: 'tr', label: words.tr },
    { tag: 'vi', label: words.vi },
    { tag: 'zh-Hans', label: words.zhHans },
  ];
}

export function speedOptions(): readonly { readonly id: string; readonly label: string }[] {
  const rate = text().settings.control.speed.rate;
  return PLAYBACK_RATES.map((value) => ({ id: String(value), label: rate(value) }));
}

/**
 * `null` first — "no preference" omits the parameter, which is not the same as sending `US`.
 * Names on the left, codes on the right (2026-08-02), sorted by name: a reader looks for
 * "Germany", not for ISO 3166. Both functions come from the caller's `Formatters` — `Intl` stays
 * in `format/` (R7's rule), and this stays a list builder.
 */
export function regionOptions(
  regionName: (code: string) => string,
  compare: (a: string, b: string) => number,
): readonly { readonly id: string; readonly label: string; readonly detail?: string }[] {
  return [
    { id: '', label: text().settings.control.region.none },
    ...CONTENT_REGIONS.map((code) => ({ id: code, label: regionName(code), detail: code })).sort(
      (a, b) => compare(a.label, b.label),
    ),
  ];
}

/* ------------------------------------------------------------------------------------------------
 * The patch builders
 *
 * One per control, and they are here rather than in the `.tsx` for R13's reason applied literally: a
 * quality choice is not "the value of a select", it is which arm of `VideoQualityPref` to build and whether
 * the codec rides along. That decision is logic, so it belongs beside the other logic — and it keeps the
 * view free of core's shapes, which is what R13 is protecting.
 * ---------------------------------------------------------------------------------------------- */

/** The forced codec, which rides on **whichever** arm is current — §7: it is orthogonal to a height. */
function codecOf(settings: VisiousSettings): { codec?: 'avc' } {
  return settings.playback.quality.codec === 'avc' ? { codec: 'avc' } : {};
}

export function qualityPatch(settings: VisiousSettings, id: string): Partial<VisiousSettings> {
  const height = Number(id);
  const quality =
    id === QUALITY_AUTO || !Number.isFinite(height)
      ? ({ kind: 'auto', ...codecOf(settings) } as const)
      : ({ kind: 'pinned', height, ...codecOf(settings) } as const);
  return { playback: { ...settings.playback, quality } };
}

export function forceAvcPatch(settings: VisiousSettings, on: boolean): Partial<VisiousSettings> {
  const quality = settings.playback.quality;
  if (on) {
    return { playback: { ...settings.playback, quality: { ...quality, codec: 'avc' } } };
  }
  // Rebuilt without the key rather than set to `undefined`, so the stored document does not carry a
  // dead field that a future reader has to interpret.
  return {
    playback: {
      ...settings.playback,
      quality:
        quality.kind === 'pinned'
          ? { kind: 'pinned', height: quality.height }
          : { kind: 'auto', ...(quality.maxHeight === undefined ? {} : { maxHeight: quality.maxHeight }) },
    },
  };
}

/** The Watch on menu's YouTube swap row — the escape hatch's one switch. */
export function youtubeEmbedEnabledPatch(
  settings: VisiousSettings,
  on: boolean,
): Partial<VisiousSettings> {
  return { youtubeEmbed: { ...settings.youtubeEmbed, enabled: on } };
}

export function speedPatch(settings: VisiousSettings, id: string): Partial<VisiousSettings> {
  const rate = Number(id);
  if (!Number.isFinite(rate)) return {};
  return { playback: { ...settings.playback, playbackRate: rate } };
}

/** `''` is "Original" — §7's `auto`, which means the language the video was recorded in. */
export function audioPatch(settings: VisiousSettings, tag: string): Partial<VisiousSettings> {
  return {
    playback: {
      ...settings.playback,
      audio:
        tag === ''
          ? { kind: 'auto' }
          : {
              kind: 'lang',
              code: tag,
              // `allowDub: true` for `withAudioSelection`'s measured reason: on this corpus every
              // language but one *is* a dub, so filtering them out makes most choices unselectable.
              // No `drc` — the stored preference never carries it; `useStableVolume` is the choice.
              allowDub: true,
            },
    },
  };
}

/** `''` is Off. Auto-generated captions are allowed: on most videos they are the only ones. */
export function subtitlePatch(settings: VisiousSettings, tag: string): Partial<VisiousSettings> {
  return {
    playback: {
      ...settings.playback,
      subtitle: tag === '' ? { kind: 'off' } : { kind: 'lang', code: tag, allowAutoGenerated: true },
    },
  };
}

export function themePatch(value: string): Partial<VisiousSettings> {
  return { theme: value === 'dark' || value === 'light' ? value : 'system' };
}

export function landingPatch(value: string): Partial<VisiousSettings> {
  return { landing: normalizeLanding(value) };
}

/** `''` is "no preference", which omits `region=` rather than sending it blank (`feed/region.ts`). */
export function regionPatch(value: string): Partial<VisiousSettings> {
  return { contentRegion: value === '' ? null : value };
}

/** Which select value represents the current quality. */
export function qualitySelectValue(settings: VisiousSettings): string {
  return settings.playback.quality.kind === 'pinned'
    ? String(settings.playback.quality.height)
    : QUALITY_AUTO;
}

export function audioSelectValue(settings: VisiousSettings): string {
  return settings.playback.audio.kind === 'lang' ? settings.playback.audio.code : '';
}

export function subtitleSelectValue(settings: VisiousSettings): string {
  return settings.playback.subtitle.kind === 'lang' ? settings.playback.subtitle.code : '';
}

export function forceAvcOn(settings: VisiousSettings): boolean {
  return settings.playback.quality.codec === 'avc';
}

// ---------------------------------------------------------------------------
// The sync section — the instance is the source of truth, the browser its cache
// ---------------------------------------------------------------------------

/**
 * Where this browser stands with the account's preferences: the reading, then what it means.
 *
 * Its own shape rather than `ProblemCopy`, which is the *failure* taxonomy — all three of these
 * states are healthy, and the `retryable: false` they used to carry was a field with nothing to say.
 * A sync that actually failed is still `ProblemCopy`, on `SyncSectionState.problem`.
 */
export interface SyncStateCopy {
  /** Two or three words, shown as the loudest thing in the box. */
  readonly status: string;
  readonly detail: string;
}

export function syncStateCopy(): Readonly<Record<SettingsSyncState, SyncStateCopy>> {
  const words = text().settings.syncState;
  return { 'signed-out': words.signedOut, syncing: words.syncing, synced: words.synced };
}

/**
 * How loud the status reads. Named for the *reading* and not for a colour, and decided here because
 * the view may not see `SettingsSyncState` at all: it comes from core, and a `.tsx` never imports
 * core (R2). Three tones for three states today, but they are separate vocabularies on purpose —
 * a fourth state would not have to invent a fourth colour.
 */
export type SyncTone = 'idle' | 'pending' | 'ok';

export function syncTone(state: SettingsSyncState): SyncTone {
  switch (state) {
    case 'synced':
      return 'ok';
    case 'syncing':
      return 'pending';
    case 'signed-out':
      return 'idle';
  }
}

export interface SyncSectionState {
  readonly state: SettingsSyncState;
  readonly tone: SyncTone;
  readonly copy: SyncStateCopy;
  readonly lastSyncedText: string | null;
  readonly busy: boolean;
  /** A failed read or write, already turned into a sentence. */
  readonly problem: ProblemCopy | null;
}

/** Copy for the two ways `/api/v1/auth/preferences` can refuse, on top of the shared taxonomy. */
export function describePreferencesProblem(problem: PreferencesProblem): ProblemCopy {
  const words = text().settings.preferences;
  // The offending field names went with the sentence: `max_results` and `volume` are the two that bite,
  // and neither is a thing the reader set or can correct. What is left is the part that is theirs —
  // nothing was changed, and it is not their doing.
  if (problem.kind === 'unwritable') return words.unwritable;
  return describeAuthProblem(problem, {
    notFound: words.notFound,
    unreadable: () => words.unreadable,
  });
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export interface SettingsScreenState {
  readonly settings: VisiousSettings;
  readonly instance: {
    readonly url: string;
    readonly host: string | null;
    readonly source: InstanceSource | null;
    readonly sourceText: string | null;
    readonly field: InstanceFieldState;
  };
  readonly sync: SyncSectionState;
}

/** Whether switching is offered: a valid, different address that a probe has approved. */
export function canSwitchInstance(input: {
  readonly field: InstanceFieldState;
  readonly report: InstanceProbeReport | null;
  readonly probing: boolean;
}): boolean {
  if (input.probing) return false;
  if (input.field.normalized === null || input.field.unchanged) return false;
  // §3: no automatic failover and no unchecked switch. Through `probeBlocksUse` rather than reading
  // `usable` here, because "what disqualifies an instance" is one policy and it lives with the probe —
  // this page must not be a second place that decides it.
  return input.report !== null && !probeBlocksUse(input.report);
}

export interface InvidiousPageLink {
  readonly href: string;
  readonly label: string;
  readonly detail: string;
}

/**
 * The two Invidious pages this app deliberately does not rebuild: instance-level preferences (the
 * admin half of `/preferences`, on the Instance tab) and the import/export tools (`/data_control`,
 * on the Data tab with the rest of the data story). Links rather than features, because both
 * operate on the account through the instance's own session — Visious's narrow token has no scope
 * for either. Two functions rather than one list because they render on different tabs.
 */
export function instancePreferencesLink(instanceUrl: string): InvidiousPageLink | null {
  const words = text().settings.instance;
  const base = parseInstanceUrl(instanceUrl);
  if (base === null) return null;
  return {
    href: new URL('/preferences', base).toString(),
    label: words.openPreferences,
    detail: words.openPreferencesDetail,
  };
}

export function dataControlLink(instanceUrl: string): InvidiousPageLink | null {
  const words = text().settings.data.importExport;
  const base = parseInstanceUrl(instanceUrl);
  if (base === null) return null;
  return {
    href: new URL('/data_control', base).toString(),
    label: words.label,
    detail: words.detail,
  };
}

const parseInstanceUrl = (instanceUrl: string): URL | null => {
  try {
    return new URL(instanceUrl);
  } catch {
    return null;
  }
};

/**
 * What switching instances costs, spelled out because R9 makes it destructive in a way nothing on
 * screen would otherwise show.
 *
 * `clearOtherInstances()` runs on every boot and deletes every stored token that is not the *current*
 * instance's. So switching away does not park the old credential for later — it deletes it on the next
 * page load, and coming back means signing in again. Saying "you will be signed out" would be the
 * comfortable half of that sentence.
 */
export function instanceSwitchWarning(): string {
  return text().settings.instance.switchWarning;
}
